export const DEFAULT_IMAGE_FRAME = {
  enabled: false, style: "rounded", color: "#ffffff", opacity: 0.85,
  inset: 2.4, thickness: 3, radius: 3, ornamentSize: 1,
};

export const IMAGE_FRAME_STYLES = [
  { id: "rounded", name: "Simple" },
  { id: "double", name: "Double line" },
  { id: "corners", name: "Corners" },
  { id: "ornate", name: "Ornate" },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const number = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;

export const resolveImageFrame = (settings) => {
  const frame = { ...DEFAULT_IMAGE_FRAME, ...settings };
  return {
    ...frame,
    style: IMAGE_FRAME_STYLES.some((item) => item.id === frame.style) ? frame.style : "rounded",
    color: /^#[a-f0-9]{6}$/i.test(frame.color) ? frame.color : DEFAULT_IMAGE_FRAME.color,
    opacity: clamp(number(frame.opacity, 0.85), 0, 1),
    inset: clamp(number(frame.inset, 2.4), 0, 12),
    thickness: clamp(number(frame.thickness, 3), 0.5, 20),
    radius: clamp(number(frame.radius, 3), 0, 20),
    ornamentSize: clamp(number(frame.ornamentSize, 1), 0.5, 2),
  };
};

const roundedRectangle = (x, y, width, height, radius) => [
  ["M", x+radius, y], ["L", x+width-radius, y], ["Q", x+width, y, x+width, y+radius],
  ["L", x+width, y+height-radius], ["Q", x+width, y+height, x+width-radius, y+height],
  ["L", x+radius, y+height], ["Q", x, y+height, x, y+height-radius],
  ["L", x, y+radius], ["Q", x, y, x+radius, y], ["Z"],
];

// Shared vector geometry keeps the SVG preview and Canvas downloads identical.
// All distances scale with the shorter side, including automatically enlarged exports.
export const getImageFramePaths = (scene, settings) => {
  const frame = resolveImageFrame(settings);
  if (!frame.enabled || frame.opacity === 0 || scene.width <= 0 || scene.height <= 0) return [];
  const short = Math.min(scene.width, scene.height), unit = short/1000;
  const stroke = frame.thickness*unit;
  const inset = Math.max(short*frame.inset/100, stroke/2+unit);
  const width = scene.width-inset*2, height = scene.height-inset*2;
  if (width <= stroke || height <= stroke) return [];
  const radius = Math.min(short*frame.radius/100, width/2, height/2);
  const path = (segments, weight = 1, opacity = 1) => ({ segments, width: stroke*weight, opacity: frame.opacity*opacity, color: frame.color });
  const cornerPaths = (segments, weight = 1, opacity = 1) => [
    [inset, inset, 1, 1], [scene.width-inset, inset, -1, 1],
    [scene.width-inset, scene.height-inset, -1, -1], [inset, scene.height-inset, 1, -1],
  ].map(([x,y,sx,sy]) => path(segments.map(([command,...points]) => [command, ...points.map((value,index) => index%2 ? y+value*sy : x+value*sx)]), weight, opacity));

  if (frame.style === "corners") {
    const length = Math.min(Math.min(width,height)*0.42, Math.max(radius+unit*3, short*0.13*frame.ornamentSize));
    return cornerPaths([["M",0,length],["L",0,radius],["Q",0,0,radius,0],["L",length,0]]);
  }
  const paths = [path(roundedRectangle(inset,inset,width,height,radius))];
  if (frame.style === "double") {
    const gap = Math.max(stroke*2, short*0.007);
    if (width > gap*2+stroke && height > gap*2+stroke) {
      paths.push(path(roundedRectangle(inset+gap,inset+gap,width-gap*2,height-gap*2,Math.max(0,radius-gap)),0.6,0.7));
    }
  }
  if (frame.style === "ornate") {
    const size = Math.min(short*0.045*frame.ornamentSize, width*0.08, height*0.08);
    const scroll = [
      ["M",radius+size*0.15,size*0.2],
      ["C",radius+size*1.05,size*0.2,radius+size*0.9,size*1.02,radius+size*0.3,size*0.9],
      ["C",radius,size*0.83,radius+size*0.05,size*0.52,radius+size*0.33,size*0.52],
      ["C",radius+size*0.53,size*0.52,radius+size*0.52,size*0.7,radius+size*0.35,size*0.7],
    ];
    const reflected = scroll.map(([command,...points]) => [command,...points.map((value,index) => points[index%2 ? index-1 : index+1])]);
    paths.push(...cornerPaths(scroll,0.7,0.9),...cornerPaths(reflected,0.7,0.9));
    const center = radius*0.68+size*0.28, half = size*0.105;
    paths.push(...cornerPaths([["M",center,center-half],["L",center+half,center],["L",center,center+half],["L",center-half,center],["Z"]],0.7));
  }
  return paths;
};

export const imageFramePathData = (segments) => segments.map((segment) => segment.join(" ")).join(" ");

export const paintImageFrame = (context, scene, settings) => {
  const paths = getImageFramePaths(scene, settings);
  if (!paths.length) return;
  context.save();
  context.filter = "none";
  context.shadowColor = "transparent"; context.shadowBlur = 0; context.shadowOffsetX = 0; context.shadowOffsetY = 0;
  context.lineCap = "round"; context.lineJoin = "round";
  const alpha = context.globalAlpha;
  for (const path of paths) {
    context.beginPath();
    for (const [command,...points] of path.segments) {
      if (command === "M") context.moveTo(...points);
      else if (command === "L") context.lineTo(...points);
      else if (command === "Q") context.quadraticCurveTo(...points);
      else if (command === "C") context.bezierCurveTo(...points);
      else context.closePath();
    }
    context.strokeStyle = path.color; context.lineWidth = path.width; context.globalAlpha = alpha*path.opacity;
    context.stroke();
  }
  context.restore();
};
